import React, {useEffect, useState} from 'react'
import {
  ClickAwayListener,
  Grow,
  MenuList,
  Paper,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import {colors, IS_ONE_GM_VISIBLE} from '@/constants'
import {ReactComponent as WalletIcon} from '@/assets/svgs/header/WalletIcon.svg'
import {ReactComponent as OctacoinIcon} from '@/assets/svgs/OctacoinIcon.svg'
import {ReactComponent as HexacoinIcon} from '@/assets/svgs/HexacoinIcon.svg'
import {ReactComponent as ReexIcon} from '@/assets/svgs/ReexIcon.svg'
import {ReactComponent as DominionIcon} from '@/assets/svgs/DominionCoinIcon.svg'
import {ReactComponent as LoyaltyIcon} from '@/assets/svgs/LoyaltyCoinIcon.svg'
import {ReactComponent as GMIcon} from '@/assets/svgs/GMCoinIcon.svg'

import {UserInterface} from '@/types/types'
import Popper from '@mui/material/Popper'
import {useAppSelector} from '@/hooks/redux'
import {
  selectDominion,
  selectHexa,
  selectLoyalty,
  selectOcta,
  selectOneGm,
  selectReex,
} from '@/store/selectors/wallet'
import Tooltip from '@/components/UI/tooltip'
import TransCustom from '@/components/UI/i18next'
import useFetching from '@/hooks/useFetching'
import UserService from '@/services/api/User'
import {errorCodeHandler} from '@/helpers/localErrorCodeHandler'

const Index = ({user}: {user: UserInterface}) => {
  const reex = useAppSelector(selectReex)
  const hexa = useAppSelector(selectHexa)
  const octa = useAppSelector(selectOcta)
  const dominion = useAppSelector(selectDominion)
  const loyalty = useAppSelector(selectLoyalty)
  const onegm = useAppSelector(selectOneGm)
  const [octaStatus, setOctaStatus] = useState<{
    coef: number
    status: string
  }>({coef: 0, status: ''})
  const [open, setOpen] = React.useState(false)
  const anchorRef = React.useRef<HTMLDivElement>(null)

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
  }

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }
    setOpen(false)
  }

  const [getDashboard] = useFetching(async () => {
    try {
      const response = await UserService.getDashboard()
      if (response.status === 200) {
        setOctaStatus({
          coef: response.data.octa_coef,
          status: response.data.promotion_status,
        })
      }
    } catch (e) {
      errorCodeHandler(e)
    }
  })

  useEffect(() => {
    void getDashboard()
  }, [])

  return (
    <>
      <WalletSVG
        ref={anchorRef}
        id="wallet-button"
        aria-controls={open ? 'wallet-menu' : undefined}
        aria-expanded={open ? 'true' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
        position="relative">
        <WalletIcon style={{fill: open ? colors.orange : colors.white}} />
      </WalletSVG>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        style={{zIndex: 1300}}>
        {({TransitionProps}) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'right top',
              marginTop: '10px',
              borderRadius: '20px',
            }}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  autoFocusItem={open}
                  id="wallet-menu"
                  aria-labelledby="wallet-button"
                  style={{
                    padding: '20px 20px',
                    background: colors.brown,
                    color: colors.white,
                    boxShadow: '0px 0px 10px 0px #FFF17233',
                    width: '350px',
                    borderRadius: '20px',
                    border: `1px solid ${colors.lightBrown}`,
                  }}>
                  <Stack direction="column" gap="20px" padding="20px">
                    <TransCustom
                      i18nKey="1181"
                      variant="button"
                      textAlign="center"
                      color={colors.white}
                      paddingBottom="20px"
                      borderBottom={`1px solid ${colors.lightBrown}`}
                    />
                    <Stack direction="column" gap="20px">
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center">
                        <Stack direction="row" gap="10px" alignItems="center">
                          <HexacoinIcon width="40px" height="40px" />
                          <Stack position="relative">
                            <Typography
                              color={colors.white}
                              fontSize="20px"
                              position="relative">
                              Hexa coin
                            </Typography>
                            <LockedMoney value={user.hexa_locked} />
                          </Stack>
                        </Stack>
                        <Typography
                          data-tooltip-id="negative-balanse-hexa"
                          color={hexa < 0 ? colors.alert : colors.white}
                          fontSize="20px">
                          {hexa}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center">
                        <Stack direction="row" gap="10px" alignItems="center">
                          <OctacoinIcon width="40px" height="40px" />
                          <Stack position="relative">
                            <Typography
                              color={colors.white}
                              fontSize="20px"
                              position="relative">
                              Octa coin
                            </Typography>
                            <LockedMoney value={user.octa_locked} />
                          </Stack>
                        </Stack>
                        <Stack position="relative">
                          <Typography color={colors.white} fontSize="20px">
                            {octa}
                          </Typography>
                          <Typography
                            position="absolute"
                            fontSize="12px"
                            noWrap
                            top="23px"
                            right="0"
                            color={colors.orange}>
                            ({octaStatus.status} + {octaStatus.coef}%)
                          </Typography>
                        </Stack>
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center">
                        <Stack direction="row" gap="10px" alignItems="center">
                          <ReexIcon width="40px" height="40px" />
                          <Stack position="relative">
                            <Typography
                              color={colors.white}
                              fontSize="20px"
                              position="relative">
                              Reex coin
                            </Typography>
                            <LockedMoney value={user.reex_locked} />
                          </Stack>
                        </Stack>
                        <Typography color={colors.white} fontSize="20px">
                          {reex}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center">
                        <Stack direction="row" gap="10px" alignItems="center">
                          <DominionIcon width="40px" height="40px" />
                          <Stack position="relative">
                            <Typography
                              color={colors.white}
                              fontSize="20px"
                              position="relative">
                              Dominion coin
                            </Typography>
                            <LockedMoney value={user.dominion_locked || 0} />
                          </Stack>
                        </Stack>
                        <Typography color={colors.white} fontSize="20px">
                          {dominion}
                        </Typography>
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center">
                        <Stack direction="row" gap="10px" alignItems="center">
                          <LoyaltyIcon width="40px" height="40px" />
                          <Typography
                            color={colors.white}
                            fontSize="20px"
                            position="relative">
                            Loyalty Coin
                            <LockedMoney
                              text="1541"
                              value={user.loyalty_required}
                            />
                          </Typography>
                        </Stack>
                        <Typography color={colors.white} fontSize="20px">
                          {loyalty}
                        </Typography>
                      </Stack>
                      {IS_ONE_GM_VISIBLE && (
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center">
                          <Stack direction="row" gap="10px" alignItems="center">
                            <GMIcon width="40px" height="40px" />
                            <Typography
                              color={colors.white}
                              fontSize="20px"
                              position="relative">
                              1GM
                            </Typography>
                          </Stack>
                          <Typography color={colors.white} fontSize="20px">
                            {onegm}
                          </Typography>
                        </Stack>
                      )}
                    </Stack>
                    {hexa < 0 && (
                      <Tooltip
                        id="negative-balanse-hexa"
                        styles={{
                          padding: '10px',
                        }}>
                        <TransCustom
                          i18nKey="1421"
                          variant="body2"
                          textAlign="center"
                        />
                      </Tooltip>
                    )}
                  </Stack>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

const LockedMoney = ({
  value,
  text = '1233',
}: {
  value: number
  text?: string
}) => {
  return (
    <Stack
      width="max-content"
      position="absolute"
      bottom="-15px"
      left="0px"
      direction="row"
      alignItems="center"
      gap="5px">
      {text === '1233' && (
        <svg
          width="15"
          height="15"
          viewBox="0 0 15 15"
          fill="none"
          xmlns="http://www.w3.org/2000/svg">
          <g clipPath="url(#clip0_3932_19649)">
            <path
              d="M12.1875 5.34188C11.923 5.5351 11.6389 5.70006 11.34 5.83406C10.3331 6.29156 8.97469 6.5625 7.5 6.5625C6.02531 6.5625 4.66688 6.29156 3.66 5.83406C3.36108 5.70006 3.07703 5.5351 2.8125 5.34188V6.5625C2.8125 6.91313 3.14625 7.38281 4.04812 7.79344C4.90687 8.18344 6.1275 8.4375 7.5 8.4375C8.12437 8.4375 8.71688 8.385 9.25875 8.29125C8.85563 8.58187 8.505 8.94094 8.22563 9.3525C7.98406 9.3678 7.74205 9.37531 7.5 9.375C6.02531 9.375 4.66688 9.10406 3.66 8.64656C3.36108 8.51256 3.07703 8.3476 2.8125 8.15438V9.375C2.8125 9.72563 3.14625 10.1953 4.04812 10.6059C4.90687 10.9959 6.1275 11.25 7.5 11.25H7.52531C7.49823 11.4991 7.49289 11.75 7.50938 12C7.50312 12.0623 7.49999 12.1249 7.5 12.1875C6.02531 12.1875 4.66688 11.9166 3.66 11.4591C3.36108 11.3251 3.07703 11.1601 2.8125 10.9669V12.1875C2.8125 12.5381 3.14625 13.0078 4.04812 13.4184C4.90687 13.8084 6.1275 14.0625 7.5 14.0625C7.5 14.4028 7.59094 14.7225 7.74937 14.9972C7.66687 14.9997 7.58375 15.0006 7.5 15C6.02531 15 4.66688 14.7291 3.66 14.2716C2.69531 13.8328 1.875 13.1316 1.875 12.1875V3.75C1.875 2.80594 2.69531 2.10469 3.66 1.66594C4.66688 1.20844 6.02531 0.9375 7.5 0.9375C8.97469 0.9375 10.3331 1.20844 11.34 1.66594C12.3047 2.10469 13.125 2.80594 13.125 3.75V7.74C12.5981 7.55412 12.0394 7.47477 11.4816 7.50656C11.9878 7.17562 12.1875 6.83344 12.1875 6.5625V5.34188ZM4.04812 2.52C3.14625 2.92875 2.8125 3.39844 2.8125 3.75C2.8125 4.10063 3.14625 4.57031 4.04812 4.98094C4.90687 5.37094 6.1275 5.625 7.5 5.625C8.8725 5.625 10.0931 5.37188 10.9519 4.98094C11.8538 4.57031 12.1875 4.09969 12.1875 3.75C12.1875 3.39937 11.8538 2.92969 10.9519 2.51906C10.0931 2.12906 8.8725 1.875 7.5 1.875C6.1275 1.875 4.90687 2.12906 4.04812 2.52Z"
              fill={colors.gray}
            />
            <path
              d="M8.4375 12.1875C8.4375 11.9389 8.53627 11.7004 8.71209 11.5246C8.8879 11.3488 9.12636 11.25 9.375 11.25V10.3125C9.375 9.81522 9.57254 9.33831 9.92418 8.98668C10.2758 8.63504 10.7527 8.4375 11.25 8.4375C11.7473 8.4375 12.2242 8.63504 12.5758 8.98668C12.9275 9.33831 13.125 9.81522 13.125 10.3125V11.25C13.3736 11.25 13.6121 11.3488 13.7879 11.5246C13.9637 11.7004 14.0625 11.9389 14.0625 12.1875V14.0625C14.0625 14.3111 13.9637 14.5496 13.7879 14.7254C13.6121 14.9012 13.3736 15 13.125 15H9.375C9.12636 15 8.8879 14.9012 8.71209 14.7254C8.53627 14.5496 8.4375 14.3111 8.4375 14.0625V12.1875ZM11.25 9.375C11.0014 9.375 10.7629 9.47377 10.5871 9.64959C10.4113 9.8254 10.3125 10.0639 10.3125 10.3125V11.25H12.1875V10.3125C12.1875 10.0639 12.0887 9.8254 11.9129 9.64959C11.7371 9.47377 11.4986 9.375 11.25 9.375Z"
              fill={colors.gray}
            />
          </g>
          <defs>
            <clipPath id="clip0_3932_19649">
              <rect width="15" height="15" fill="white" />
            </clipPath>
          </defs>
        </svg>
      )}
      <TransCustom
        color={colors.gray}
        fontSize="13px"
        lineHeight="10px"
        i18nKey={text}
      />
      <Typography color={colors.gray} fontSize="13px" lineHeight="10px">
        :
      </Typography>
      <Typography color={colors.gray} fontSize="13px" lineHeight="10px">
        {value}
      </Typography>
    </Stack>
  )
}

export default Index

const WalletSVG = styled(Stack)`
  &:hover {
    cursor: pointer;

    & > svg {
      fill: ${colors.orange} !important;
    }
  }
`
